import React from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import {
  BIG,
  CABRIO,
  ELETRIC,
  MEDIUM,
  MINIVAN,
  PREMIUM,
  SCOOTERS,
  SMALL,
  SUVS,
  VAN,
} from "../../helpers/Constants";
import { getVehicleImageUrl } from "../../helpers/getVehicleImageUrl";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CardContent,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { HashLink as Link } from "react-router-hash-link";
import { vehicleCodeChanger } from "../../helpers/vehicleCodeChanger";
import car_seats from "../../Assets/car-chair.png";
// import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
// import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
// import SettingsIcon from "@mui/icons-material/Settings";
// import DoorSlidingIcon from "@mui/icons-material/DoorSliding";
// import AirlineSeatReclineNormalIcon from "@mui/icons-material/AirlineSeatReclineNormal";
// import InfoSharpIcon from "@mui/icons-material/InfoSharp";

const responsive = {
  superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 4 },
  desktop: { breakpoint: { max: 3000, min: 1480 }, items: 4 }, // Display 4 cars
  smallDesktop: { breakpoint: { max: 1480, min: 1100 }, items: 3 }, // Display 3 cars
  tablet: { breakpoint: { max: 1100, min: 768 }, items: 2 },
  mobile: { breakpoint: { max: 768, min: 0 }, items: 1 },
};

const CarouselComponent = React.memo(
  ({ setQuoteMessage, onReserveClick, width, vehicles = [] }) => {
    const { t } = useTranslation();

    function vehicleType(code) {
      if (SCOOTERS.includes(code)) return t("scooter");
      if (BIG.includes(code)) return t("large");
      if (SUVS.includes(code)) return t("suv");
      if (SMALL.includes(code))
        return code === "NCMV"
          ? `${t("small")} ${t("withCanvasEletricRoof")}`
          : code === "NBMV"
          ? `${t("small")} ${t("withPanoramicRoof")}`
          : t("small");
      if (MEDIUM.includes(code)) return t("medium");
      if (CABRIO.includes(code)) return t("cabrio");
      if (ELETRIC.includes(code)) return "eletric";
      if (PREMIUM.includes(code)) return t("premium");
      if (MINIVAN.includes(code)) return t("miniVan");
      if (VAN.includes(code)) return t("van");
      return "Unknown category";
    }

    function checkFuelType(fuel, code) {
      if (code === "NTMV" || code === "CFMV" || code === "CDMV") {
        return t("petrol");
      }

      return fuel === "Petrol" ? t("petrol") : t("diesel");
    }

    return (
      <Carousel
        swipeable
        draggable
        infinite
        responsive={responsive}
        removeArrowOnDeviceType={["tablet", "mobile"]}
      >
        {vehicles?.map(
          (
            {
              id,
              brand,
              fuelType,
              transmission,
              picture,
              pictureSource,
              year,
              doors,
              seats,
              code,
            },
            index
          ) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: 1,
              }}
            >
              <Paper
                key={index}
                elevation={4}
                sx={{
                  borderRadius: 4,
                  padding: 2,
                  height: 550,
                  minWidth: {
                    xs: 280, // screens <600px
                    sm: 360, // 600px–900px
                    md: 360, // 900px+
                  },
                }}
              >
                {/* Image with Badge */}
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    gutterBottom
                    variant="h5"
                    align="center"
                    color="primary.main"
                    component="div"
                  >
                    {t("group")} {vehicleCodeChanger(code)}
                  </Typography>
                  <LazyLoadImage
                    alt="CAR"
                    src={getVehicleImageUrl({ picture, pictureSource })}
                    style={{
                      height: 220, // Fixed height for consistency
                      width: 300, // Fixed width
                      maxHeight: "100%", // Ensure the image doesn’t exceed the container's height
                      maxWidth: "100%", // Ensure the image doesn’t exceed the container's width
                      objectFit: "contain", // Fit the image within the container, may result in some empty space
                      display: "block", // Center the image horizontally
                      margin: "0 auto", // Center the image horizontally
                    }}
                  />
                </Box>

                {/* Car Info */}
                <CardContent>
                  {/* Brand Name */}
                  <Typography variant="h6" fontWeight="bold">
                    {brand}
                  </Typography>

                  {/* "Or Similar" Text */}
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 1 }}
                  >
                    {t("orSimilar")}{" "}
                    <Typography
                      variant="body2"
                      component={"span"}
                      sx={{ fontSize: "0.7rem", color: "gray" }}
                    >
                      ({vehicleType(code)})
                    </Typography>
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: { xs: 1, sm: 4 }, // space between each icon-text block
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title={t("fuelType")}>
                        <img
                          width="24"
                          height="24"
                          src="https://img.icons8.com/material/24/gas-station--v1.png"
                          alt="gas-station--v1"
                          color="#EE8013"
                        />
                      </Tooltip>
                      <Typography variant="body2" align="center">
                        {checkFuelType(fuelType, code)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title={t("gearbox")}>
                        <img
                          width="24"
                          height="24"
                          src="https://img.icons8.com/material/24/gearbox-selector.png"
                          alt="gearbox-selector"
                        />
                      </Tooltip>
                      <Typography variant="body2" align="center">
                        {[
                          "EDAV",
                          "MDAV",
                          "CTAV",
                          "RVAD",
                          "HDAE",
                          "CDAV",
                          "CDAD",
                          "CGAV",
                          "CGAD",
                          "DDAD",
                          "PDAD",
                          "SFAD",
                        ].includes(code)
                          ? t("automatic")
                          : t("manual")}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title={t("doors")}>
                        <img
                          width="24"
                          height="24"
                          src="https://img.icons8.com/material/24/car-door--v2.png"
                          alt="car-door--v2"
                        />
                      </Tooltip>
                      <Typography variant="body2" align="center">
                        {doors}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title={t("seats")}>
                        <img
                          width="24"
                          height="24"
                          src={car_seats}
                          alt="aircraft-seat-middle"
                        />
                      </Tooltip>
                      <Typography variant="body2" align="center">
                        {seats}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>

                {/* Actions */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingX: 2,
                    paddingBottom: 2,
                  }}
                >
                  {width < 900 ? (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          minWidth: "80px",
                          paddingX: 1,
                          paddingY: 0.5,
                          fontSize: "0.75rem",
                        }}
                        onClick={() => onReserveClick({ code, brand, picture, pictureSource })}
                      >
                        {t("reserve")}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          minWidth: "90px",
                          paddingX: 1,
                          paddingY: 0.5,
                          fontSize: "0.75rem",
                        }}
                      >
                        <Link
                          aria-hidden="true"
                          smooth
                          to="#contacts"
                          onClick={() =>
                            setQuoteMessage(
                              t("quoteMessage") + vehicleCodeChanger(code)
                            )
                          }
                        >
                          {t("requestQuote")}
                        </Link>
                      </Button>{" "}
                    </>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => onReserveClick({ code, brand, picture, pictureSource })}
                      >
                        {t("reserve")}
                      </Button>
                      <Button variant="outlined" size="small">
                        <Link
                          aria-hidden="true"
                          smooth
                          to="#contacts"
                          onClick={() =>
                            setQuoteMessage(
                              t("quoteMessage") + vehicleCodeChanger(code)
                            )
                          }
                        >
                          {t("requestQuote")}
                        </Link>
                      </Button>
                    </>
                  )}
                </Box>
                {code === "PDAD" && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Tooltip title={t("mandatoryExcess")}>
                      <img
                        width="24"
                        height="24"
                        src="https://img.icons8.com/material/24/info--v1.png"
                        alt="info--v1"
                      />
                    </Tooltip>
                    <Typography variant="body2" align="center">
                      {t("mandatoryExcess")}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )
        )}
      </Carousel>
    );
  }
);

export default CarouselComponent;
